import { modelOptions, PropType, post, prop } from "@typegoose/typegoose";
import type { SensorType } from "verdi-common";
import { z } from "zod";
import { DataTypeSpecifier } from "../../../../HistoricalData.js";
import {
    DataTypeSpecifierString,
    getZodSchemaFromClass,
    SensorTypeString,
    validate,
    valueRangeArray,
} from "../../../../zod-decorator/index.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../../../schemaBase.js";
import type {
    RecentVerificationDataPointFieldsWithDefaults,
    RecentVerificationDataPointSchemaTypes,
} from "../../../types/recentVerificationDataPoint.d.js";
import type { DtoValidatorTypes } from "../../../validatableClass.js";

const SCHEMA_KEY = "RecentVerificationDataPoint" as const;

/**
 * Sensor measurement data point collected during verification process.
 *
 * This model captures specific sensor readings that are used to validate
 * irrigation system performance during verification. Each data point includes:
 * - The measured sensor value and its type
 * - Timestamp of when the reading was taken
 * - Expected acceptable range for this valve state
 * - Whether the reading falls within acceptable parameters
 *
 * These data points are collected and analyzed to determine if sensors
 * are providing readings consistent with the intended irrigation state.
 *
 * @see SproutVerificationInstance - Contains arrays of these data points
 * @see GeneralSproutVerificationCheck - Uses this data for sensor verification
 */
@modelOptions({ schemaOptions: { ...getDefaultSchemaOptions(SCHEMA_KEY) } })
@post<SchemaBase<RecentVerificationDataPoint>>(...addSchemaKeyHook(SCHEMA_KEY))
export class RecentVerificationDataPoint extends SchemaBase<
    RecentVerificationDataPoint,
    RecentVerificationDataPointFieldsWithDefaults
> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    /** Timestamp when this sensor reading was taken */
    @validate({ zodValidator: z.coerce.date().optional() })
    @prop({ type: () => Date, required: false })
    public date?: Date;

    /** Data type specification for the sensor measurement */
    @validate({ zodValidator: DataTypeSpecifierString.optional() })
    @prop({ type: () => String, required: false })
    public dataType?: DataTypeSpecifier;

    /** The actual sensor measurement value */
    @validate({ zodValidator: z.number().optional() })
    @prop({ type: () => Number, required: false })
    public value?: number;

    /** Index identifying which sensor on the device (0, 1, etc.) */
    @validate({ zodValidator: z.number() })
    @prop({ type: () => Number, required: true })
    public sensorIndex!: number;

    /** Expected range of acceptable values for this sensor in current valve state */
    @validate({ zodValidator: valueRangeArray.optional() })
    @prop({ type: () => Number, required: false }, PropType.ARRAY)
    public acceptableValueRange?: number[];

    /** Whether the measured value falls within the acceptable range */
    @validate({ zodValidator: z.boolean().optional() })
    @prop({ type: () => Boolean, required: false })
    public acceptableValue?: boolean;

    /** Type of sensor providing this measurement (moisture, flow, pressure, etc.) */
    @validate({ zodValidator: SensorTypeString })
    @prop({ type: () => String, required: true })
    public sensorType!: SensorType;

    static getters = {} as const satisfies Record<string, (self: RecentVerificationDataPoint) => any>;

    static utilities = {} as const satisfies Record<string, (...args: any) => any>;

    static dtoValidators: DtoValidatorTypes<RecentVerificationDataPointSchemaTypes>;

    constructor(
        properties?: SchemaConstructorArgs<RecentVerificationDataPoint, RecentVerificationDataPointFieldsWithDefaults>,
    ) {
        super(properties);
        Object.assign(this, properties);
    }
}

RecentVerificationDataPoint.dtoValidators = getZodSchemaFromClass(
    RecentVerificationDataPoint,
) as unknown as DtoValidatorTypes<RecentVerificationDataPointSchemaTypes>;
